import type { PublishedSnapshot } from '../api/client'
import { isRenderableCanvasNodeType } from '../utils/nodeTypes'

type PublishedSnapshotNode = PublishedSnapshot['snapshot']['nodes'][number]
type SnapshotPosition = PublishedSnapshotNode['position']
type PublishedSnapshotNodeIdentity = Pick<
  PublishedSnapshotNode,
  'id' | 'type' | 'parentNode' | 'extent' | 'position'
>

export type PublishedSnapshotPlacement = {
  position: SnapshotPosition
  parentId?: string
  extent?: PublishedSnapshotNode['extent']
}

function assertAcyclicParentChain(
  node: PublishedSnapshotNodeIdentity,
  nodeById: ReadonlyMap<string, PublishedSnapshotNodeIdentity>,
): void {
  const visited = new Set([node.id])
  let parentId = node.parentNode
  while (parentId) {
    if (visited.has(parentId)) {
      throw new Error(`Published snapshot contains a parent cycle at ${parentId}`)
    }
    visited.add(parentId)
    parentId = nodeById.get(parentId)?.parentNode
  }
}

export function preparePublishedSnapshotNodes<T extends PublishedSnapshotNodeIdentity>(
  nodes: readonly T[],
): {
  nodes: T[]
  placements: ReadonlyMap<string, PublishedSnapshotPlacement>
} {
  const nodeById = new Map(nodes.map((node) => [node.id, node]))
  const renderableNodes = nodes.filter((node) => isRenderableCanvasNodeType(node.type))
  const renderableNodeIds = new Set(renderableNodes.map((node) => node.id))
  const placements = new Map<string, PublishedSnapshotPlacement>()

  for (const node of renderableNodes) {
    assertAcyclicParentChain(node, nodeById)
    const position = { ...node.position }
    let parentId = node.parentNode

    while (parentId && !renderableNodeIds.has(parentId)) {
      const filteredParent = nodeById.get(parentId)
      if (!filteredParent) {
        parentId = undefined
        break
      }
      position.x += filteredParent.position.x
      position.y += filteredParent.position.y
      parentId = filteredParent.parentNode
    }

    placements.set(node.id, {
      position,
      parentId,
      extent: node.extent === 'parent' && parentId == null ? undefined : node.extent,
    })
  }

  return { nodes: renderableNodes, placements }
}
